import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutGrid, 
  Copy, 
  Check,
  Trash2, 
  Calendar,
  Clock,
  CheckCircle,
  RefreshCw,
  MessageSquare,
  Hash,
  Image,
  Download,
  Share2,
  Facebook,
  Instagram,
  Loader2,
  CheckCircle2,
  XCircle,
  Plus,
  Video,
  Upload,
  Sparkles,
  Play,
  Film,
  Wand2,
  Mic,
  Music,
  FileText
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PublicationsTab() {
  const { currentBusiness, businessId } = useBusiness();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [copiedItem, setCopiedItem] = useState("");
  const [publishingId, setPublishingId] = useState(null);
  const [socialStatus, setSocialStatus] = useState(null);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [selectedPub, setSelectedPub] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState(["facebook", "instagram"]);
  const [publishResults, setPublishResults] = useState(null);
  
  // Estado para imágenes cargadas bajo demanda
  const [loadedImages, setLoadedImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  
  // New Reel states
  const [showReelModal, setShowReelModal] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [savingReel, setSavingReel] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [uploadedVideoId, setUploadedVideoId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [reelData, setReelData] = useState({
    name: "",
    copy: "",
    hashtags: [],
    fixedComment: ""
  });
  const videoInputRef = useRef(null);

  // Motion states
  const [showMotionModal, setShowMotionModal] = useState(false);
  const [storiesGallery, setStoriesGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [selectedMotionImage, setSelectedMotionImage] = useState(null);
  const [generatingMotion, setGeneratingMotion] = useState(false);
  const [motionData, setMotionData] = useState({
    animationPrompt: "",
    voiceoverText: "",
    sunoPrompt: ""
  });

  // Generate fixed comment based on business
  const getFixedComment = () => {
    if (businessId === "maquinaria") {
      return `🏗️ ¡Tu próxima máquina te espera!
📲 Escríbenos: ${currentBusiness?.phone || "55 1318 1745"}
💰 Financiamiento disponible
🌐 ${currentBusiness?.website || "maquinaria.hebeh.com.mx"}`;
    }
    return `🚗 ¡Tu próximo vehículo te espera!
📲 Escríbenos: ${currentBusiness?.phone || "55 1318 1745"}
💰 Financiamiento disponible a tu medida
🌐 ${currentBusiness?.website || "automotriz.hebeh.com.mx"}`;
  };

  const getCompanyHashtags = () => {
    if (businessId === "maquinaria") {
      return "#HEBEH #HEBEHMaquinaria #Construcción";
    }
    return "#HEBEH #HEBEHAutomotriz";
  };

  useEffect(() => {
    fetchPublications();
    fetchSocialStatus();
  }, [businessId]);

  const fetchSocialStatus = async () => {
    try {
      const response = await axios.get(`${API}/social/status`);
      setSocialStatus(response.data);
    } catch (error) {
      console.error("Error fetching social status:", error);
    }
  };

  const fetchPublications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/publications?business_id=${businessId}`);
      if (response.data.success) {
        setPublications(response.data.publications);
      }
    } catch (error) {
      toast.error("Error al cargar publicaciones");
    } finally {
      setLoading(false);
    }
  };

  // Cargar imágenes completas de una publicación bajo demanda
  const loadPublicationImages = async (pubId) => {
    if (loadedImages[pubId] || loadingImages[pubId]) return;
    
    setLoadingImages(prev => ({ ...prev, [pubId]: true }));
    try {
      const response = await axios.get(`${API}/publications/${pubId}`);
      if (response.data.success && response.data.publication) {
        setLoadedImages(prev => ({ 
          ...prev, 
          [pubId]: response.data.publication.images || []
        }));
      }
    } catch (error) {
      console.error("Error loading publication images:", error);
    } finally {
      setLoadingImages(prev => ({ ...prev, [pubId]: false }));
    }
  };

  // Obtener imágenes de una publicación (cargadas o truncadas)
  const getPublicationImages = (pub) => {
    // Si ya cargamos las imágenes completas, usarlas
    if (loadedImages[pub.id]) {
      return loadedImages[pub.id];
    }
    // Si no, devolver las truncadas (para mostrar placeholder)
    return pub.images || [];
  };

  const updateStatus = async (pubId, newStatus) => {
    try {
      await axios.put(`${API}/publications/${pubId}/status?status=${newStatus}`);
      toast.success("Estado actualizado");
      fetchPublications();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const deletePublication = async (pubId) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await axios.delete(`${API}/publications/${pubId}`);
      toast.success("Publicación eliminada");
      fetchPublications();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const copyToClipboard = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopiedItem(""), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const getFullCopyText = (pub, type = "feed") => {
    const copy = type === "feed" ? pub.copy_feed : pub.copy_stories;
    const hashtags = type === "feed" ? pub.hashtags_feed : pub.hashtags_stories;
    // Orden: Copy -> Hashtags del vehículo -> Hashtags de la empresa -> Comentario fijo
    return `${copy}\n\n${hashtags.join(" ")} ${getCompanyHashtags()}\n\n---\n\n${getFixedComment()}`;
  };

  // =============================================
  // FUNCIONES PARA REELS
  // =============================================
  
  const openReelModal = () => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setUploadedVideoId(null);
    setUploadProgress(0);
    setReelData({
      name: "",
      copy: "",
      hashtags: [],
      fixedComment: ""
    });
    setShowReelModal(true);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Tipo de archivo no soportado. Use MP4, MOV, AVI o WebM.");
        return;
      }
      
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error("El video es muy grande. Máximo 100MB.");
        return;
      }
      
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
    }
  };

  const uploadVideo = async () => {
    if (!videoFile) return;
    
    setUploadingVideo(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('business_id', businessId);
      
      const response = await axios.post(`${API}/reels/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      if (response.data.success) {
        setUploadedVideoId(response.data.video_id);
        setUploadProgress(100);
        toast.success("Video subido exitosamente");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir el video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const generateReelCopy = async () => {
    if (!uploadedVideoId) {
      toast.error("Primero sube el video");
      return;
    }
    
    setGeneratingCopy(true);
    
    try {
      const response = await axios.post(`${API}/reels/generate-copy`, {
        video_id: uploadedVideoId,
        business_id: businessId
      });
      
      if (response.data.success) {
        setReelData({
          name: response.data.suggested_name || "",
          copy: response.data.copy_feed || "",
          hashtags: response.data.hashtags_feed || [],
          fixedComment: response.data.fixed_comment || getFixedComment()
        });
        toast.success("Copy generado exitosamente");
        
        if (response.data.warning) {
          toast.warning(response.data.warning);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar copy");
    } finally {
      setGeneratingCopy(false);
    }
  };

  const saveReelPublication = async () => {
    if (!uploadedVideoId || !reelData.name || !reelData.copy) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    
    setSavingReel(true);
    
    try {
      const response = await axios.post(`${API}/reels/create-publication`, {
        business_id: businessId,
        vehicle_name: reelData.name,
        video_url: uploadedVideoId,
        copy_feed: reelData.copy,
        hashtags_feed: reelData.hashtags,
        fixed_comment: reelData.fixedComment
      });
      
      if (response.data.success) {
        toast.success("Publicación de reel creada");
        setShowReelModal(false);
        fetchPublications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear publicación");
    } finally {
      setSavingReel(false);
    }
  };

  // =============================================
  // FUNCIONES PARA MOTION
  // =============================================

  const openMotionModal = async () => {
    setShowMotionModal(true);
    setSelectedMotionImage(null);
    setMotionData({ animationPrompt: "", voiceoverText: "", sunoPrompt: "" });
    setLoadingGallery(true);
    
    try {
      const response = await axios.get(`${API}/motion/stories-gallery?business_id=${businessId}`);
      if (response.data.success) {
        setStoriesGallery(response.data.images);
      }
    } catch (error) {
      toast.error("Error al cargar galería de stories");
    } finally {
      setLoadingGallery(false);
    }
  };

  const generateMotionPrompts = async () => {
    if (!selectedMotionImage) {
      toast.error("Selecciona una imagen primero");
      return;
    }
    
    setGeneratingMotion(true);
    
    try {
      const response = await axios.post(`${API}/motion/generate`, {
        image_url: selectedMotionImage.image_url,
        business_id: businessId,
        publication_id: selectedMotionImage.publication_id
      });
      
      if (response.data.success) {
        setMotionData({
          animationPrompt: response.data.animation_prompt,
          voiceoverText: response.data.voiceover_text,
          sunoPrompt: response.data.suno_prompt
        });
        toast.success("Prompts generados exitosamente");
        
        if (response.data.warning) {
          toast.warning(response.data.warning);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar prompts");
    } finally {
      setGeneratingMotion(false);
    }
  };

  const copyMotionText = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(`motion-${type}`);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiedItem(""), 2000);
  };

  // Función para abrir el modal de publicación social
  const openSocialModal = async (pub) => {
    setSelectedPub(pub);
    setSelectedImageIndex(0);
    setSelectedPlatforms(["facebook", "instagram"]);
    setPublishResults(null);
    setShowSocialModal(true);
    
    // Cargar imágenes completas si no están disponibles
    if (!loadedImages[pub.id] && pub.images?.some(img => img.url?.includes("[TRUNCATED]"))) {
      await loadPublicationImages(pub.id);
    }
  };

  // Función para publicar en redes sociales
  const publishToSocial = async () => {
    if (!selectedPub || selectedPlatforms.length === 0) return;
    
    setPublishingId(selectedPub.id);
    setPublishResults(null);
    
    try {
      const response = await axios.post(`${API}/publications/${selectedPub.id}/social`, {
        publication_id: selectedPub.id,
        platforms: selectedPlatforms,
        image_index: selectedImageIndex
      }, {
        timeout: 600000  // 10 minutes timeout for large video uploads
      });
      
      setPublishResults(response.data.results);
      
      if (response.data.all_success) {
        toast.success("Publicado exitosamente en todas las plataformas");
      } else if (response.data.success) {
        // Indicar EXACTAMENTE cuáles fallaron y por qué
        const failed = (response.data.results || []).filter(r => !r.success);
        const okList = (response.data.results || []).filter(r => r.success).map(r => r.platform).join(", ");
        const failedSummary = failed.map(r => `${r.platform}: ${r.error || 'error desconocido'}`).join(" | ");
        toast.warning(
          `Publicado en ${okList || '—'}. Falló en: ${failedSummary}`,
          { duration: 10000 }
        );
      } else {
        const errs = (response.data.results || []).map(r => `${r.platform}: ${r.error || 'error'}`).join(" | ");
        toast.error(`Error al publicar en redes sociales. ${errs}`, { duration: 10000 });
      }
      
      fetchPublications();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al publicar";
      toast.error(errorMsg);
      setPublishResults([{
        platform: "general",
        success: false,
        error: errorMsg
      }]);
    } finally {
      setPublishingId(null);
    }
  };

  const filteredPublications = filterStatus 
    ? publications.filter(p => p.status === filterStatus)
    : publications;

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: "bg-yellow-500/20 text-yellow-400",
      programada: "bg-blue-500/20 text-blue-400",
      publicada: "bg-green-500/20 text-green-400"
    };
    const icons = {
      pendiente: <Clock className="w-3 h-3" />,
      programada: <Calendar className="w-3 h-3" />,
      publicada: <CheckCircle className="w-3 h-3" />
    };
    return (
      <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-montserrat font-bold text-white flex items-center gap-3">
          <LayoutGrid className="w-7 h-7 text-orange-500" />
          Parrilla de Publicaciones
          <span className="text-sm font-normal text-slate-400 ml-2">
            ({publications.length} publicaciones)
          </span>
        </h2>
        <div className="flex items-center gap-3">
          {/* Add New Reel Button */}
          <button
            onClick={openReelModal}
            data-testid="add-reel-button"
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white font-medium flex items-center gap-2 transition-all"
          >
            <Plus className="w-5 h-5" />
            <Film className="w-4 h-4" />
            Agregar Reel
          </button>
          {/* Create Motion Button - Only for Maquinaria */}
          {businessId === 'maquinaria' && (
            <button
              onClick={openMotionModal}
              data-testid="create-motion-button"
              className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg text-white font-medium flex items-center gap-2 transition-all"
            >
              <Wand2 className="w-5 h-5" />
              Crear Motion
            </button>
          )}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="programada">Programadas</option>
            <option value="publicada">Publicadas</option>
          </select>
          <button
            onClick={fetchPublications}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Publications Grid */}
      {loading ? (
        <div className="glass-card rounded-lg p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-400">Cargando publicaciones...</p>
        </div>
      ) : filteredPublications.length === 0 ? (
        <div className="glass-card rounded-lg p-12 text-center">
          <LayoutGrid className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No hay publicaciones</p>
          <p className="text-slate-500 text-sm mt-2">
            Genera contenido en la pestaña Diseño y confirma para agregarlo aquí
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredPublications.map((pub) => (
            <div key={pub.id} className="glass-card rounded-lg overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium">{pub.vehicle_name}</h3>
                  <p className="text-slate-500 text-sm">
                    {new Date(pub.created_at).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(pub.status)}
                  <button
                    onClick={() => deletePublication(pub.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Video/Reel Preview */}
              {pub.is_reel && pub.video_url && (
                <div className="p-4 border-b border-slate-800">
                  <h4 className="text-sm text-slate-400 mb-3 flex items-center gap-2">
                    <Film className="w-4 h-4 text-purple-400" />
                    Video / Reel
                  </h4>
                  <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                    <video 
                      src={`${API}/reels/video/${pub.video_url}`}
                      controls
                      className="w-full h-full object-contain"
                    />
                    <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Film className="w-3 h-3" />
                      Reel
                    </div>
                  </div>
                </div>
              )}

              {/* Images */}
              {pub.images && pub.images.length > 0 && !pub.is_reel && (
                <div className="p-4 border-b border-slate-800">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm text-slate-400 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Imágenes ({pub.images.length})
                    </h4>
                    {!loadedImages[pub.id] && !loadingImages[pub.id] && (
                      <button
                        onClick={() => loadPublicationImages(pub.id)}
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Cargar imágenes
                      </button>
                    )}
                    {loadingImages[pub.id] && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Cargando...
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {getPublicationImages(pub).map((img, idx) => {
                      const isImageLoaded = loadedImages[pub.id] || (img.url && !img.url.includes("[TRUNCATED]"));
                      
                      const downloadImage = () => {
                        if (!img.url || img.url.includes("[TRUNCATED]")) {
                          // Cargar primero las imágenes
                          loadPublicationImages(pub.id);
                          return;
                        }
                        
                        const link = document.createElement('a');
                        link.href = img.url;
                        link.download = `${img.name?.replace(/\s/g, '_') || 'imagen_' + idx}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success(`Descargando ${img.name || 'imagen'}`);
                      };
                      
                      return (
                        <div key={idx} className="relative group">
                          {isImageLoaded && img.url && !img.url.includes("[TRUNCATED]") ? (
                            <>
                              <img 
                                src={img.url} 
                                alt={img.name}
                                className="w-full aspect-square object-cover rounded"
                              />
                              <button
                                onClick={downloadImage}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded cursor-pointer"
                              >
                                <Download className="w-5 h-5 text-white" />
                              </button>
                            </>
                          ) : (
                            <div 
                              className="w-full aspect-square bg-slate-700 rounded flex items-center justify-center cursor-pointer hover:bg-slate-600 transition-colors"
                              onClick={() => loadPublicationImages(pub.id)}
                            >
                              {loadingImages[pub.id] ? (
                                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                              ) : (
                                <Image className="w-4 h-4 text-slate-500" />
                              )}
                            </div>
                          )}
                          <p className="text-xs text-slate-500 text-center mt-1 truncate">{img.name}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Copy Feed */}
              <div className="p-4 border-b border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm text-slate-400 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Copy Feed
                  </h4>
                  <button
                    onClick={() => copyToClipboard(getFullCopyText(pub, "feed"), `copy-feed-${pub.id}`)}
                    className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                  >
                    {copiedItem === `copy-feed-${pub.id}` ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-line line-clamp-3">
                  {pub.copy_feed}
                </p>
              </div>

              {/* Hashtags */}
              <div className="p-4 border-b border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm text-slate-400 flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Hashtags
                  </h4>
                  <button
                    onClick={() => copyToClipboard(pub.hashtags_feed.join(" "), `hashtags-${pub.id}`)}
                    className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                  >
                    {copiedItem === `hashtags-${pub.id}` ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-blue-400">
                  {pub.hashtags_feed.slice(0, 5).join(" ")}
                  {pub.hashtags_feed.length > 5 && "..."} 
                  <span className="text-orange-400 ml-1">{getCompanyHashtags()}</span>
                </p>
              </div>

              {/* Fixed Comment */}
              <div className="p-4 border-b border-slate-800 bg-slate-800/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm text-orange-400 font-medium">Comentario Fijo</h4>
                  <button
                    onClick={() => copyToClipboard(pub.fixed_comment || getFixedComment(), `comment-${pub.id}`)}
                    className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                  >
                    {copiedItem === `comment-${pub.id}` ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-slate-300 whitespace-pre-line">
                  {pub.fixed_comment || getFixedComment()}
                </p>
              </div>

              {/* Social Media Publish Button */}
              <div className="p-4 border-b border-slate-800">
                <button
                  onClick={() => openSocialModal(pub)}
                  disabled={(!pub.images || pub.images.length === 0) && !pub.is_reel}
                  data-testid={`publish-social-${pub.id}`}
                  className={`w-full py-3 rounded-lg flex items-center justify-center gap-3 font-medium transition-all ${
                    (pub.images && pub.images.length > 0) || pub.is_reel
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white"
                      : "bg-slate-700 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  <Share2 className="w-5 h-5" />
                  {pub.is_reel ? "Publicar Reel" : "Publicar en Redes Sociales"}
                  {pub.social_published && (
                    <CheckCircle2 className="w-4 h-4 text-green-400 ml-1" />
                  )}
                </button>
                {pub.social_published && pub.social_publish_history && (
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Última publicación: {new Date(pub.social_publish_history.published_at).toLocaleDateString('es-MX')}
                  </p>
                )}
              </div>

              {/* Status Actions */}
              <div className="p-4 flex gap-2">
                {pub.status !== "pendiente" && (
                  <button
                    onClick={() => updateStatus(pub.id, "pendiente")}
                    className="flex-1 py-2 rounded bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 text-sm transition-colors"
                  >
                    Pendiente
                  </button>
                )}
                {pub.status !== "programada" && (
                  <button
                    onClick={() => updateStatus(pub.id, "programada")}
                    className="flex-1 py-2 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-sm transition-colors"
                  >
                    Programar
                  </button>
                )}
                {pub.status !== "publicada" && (
                  <button
                    onClick={() => updateStatus(pub.id, "publicada")}
                    className="flex-1 py-2 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 text-sm transition-colors"
                  >
                    Publicada
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Social Media Publish Modal */}
      {showSocialModal && selectedPub && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-500" />
                Publicar en Redes Sociales
              </h3>
              <button
                onClick={() => setShowSocialModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Vehicle Info */}
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-white font-medium flex items-center gap-2">
                  {selectedPub.is_reel && <Film className="w-4 h-4 text-purple-400" />}
                  {selectedPub.vehicle_name}
                </p>
                <p className="text-slate-400 text-sm">
                  {selectedPub.is_reel 
                    ? "Video/Reel para publicar" 
                    : `${selectedPub.images?.length || 0} imágenes disponibles`}
                </p>
              </div>

              {/* Video Preview for Reels */}
              {selectedPub.is_reel && selectedPub.video_url && (
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Vista previa del Reel:</label>
                  <div className="bg-black rounded-lg overflow-hidden aspect-video">
                    <video 
                      src={`${API}/reels/video/${selectedPub.video_url}`}
                      controls
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Image Selection - Only for non-reels */}
              {!selectedPub.is_reel && selectedPub.images?.length > 0 && (
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Seleccionar imagen:</label>
                {loadingImages[selectedPub.id] && (
                  <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Cargando imágenes...
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2">
                  {(loadedImages[selectedPub.id] || selectedPub.images)?.map((img, idx) => {
                    const hasValidUrl = img.url && !img.url.includes("[TRUNCATED]");
                    return (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === idx
                          ? "border-blue-500 ring-2 ring-blue-500/30"
                          : "border-slate-700 hover:border-slate-500"
                      }`}
                    >
                      {hasValidUrl ? (
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-full aspect-square object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-square bg-slate-700 flex items-center justify-center">
                          {loadingImages[selectedPub.id] ? (
                            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                          ) : (
                            <Image className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                      )}
                      {selectedImageIndex === idx && (
                        <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-blue-400" />
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 text-center p-1 truncate bg-slate-800/80">
                        {img.name}
                      </p>
                    </button>
                  )})}
                </div>
              </div>
              )}

              {/* Platform Selection */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Plataformas:</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (selectedPlatforms.includes("facebook")) {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== "facebook"));
                      } else {
                        setSelectedPlatforms([...selectedPlatforms, "facebook"]);
                      }
                    }}
                    className={`flex-1 py-3 rounded-lg flex items-center justify-center gap-2 border-2 transition-all ${
                      selectedPlatforms.includes("facebook")
                        ? "border-blue-500 bg-blue-500/20 text-blue-400"
                        : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    <Facebook className="w-5 h-5" />
                    Facebook
                    {socialStatus?.facebook?.configured && (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (selectedPlatforms.includes("instagram")) {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== "instagram"));
                      } else {
                        setSelectedPlatforms([...selectedPlatforms, "instagram"]);
                      }
                    }}
                    className={`flex-1 py-3 rounded-lg flex items-center justify-center gap-2 border-2 transition-all ${
                      selectedPlatforms.includes("instagram")
                        ? "border-pink-500 bg-pink-500/20 text-pink-400"
                        : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    <Instagram className="w-5 h-5" />
                    Instagram
                    {socialStatus?.instagram?.configured && (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Status Warning */}
              {socialStatus && (!socialStatus.facebook?.configured || !socialStatus.instagram?.configured) && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm">
                    {!socialStatus.facebook?.configured && !socialStatus.instagram?.configured
                      ? "Las credenciales de redes sociales no están configuradas."
                      : !socialStatus.facebook?.configured
                      ? "Facebook no está configurado."
                      : "Instagram no está configurado."}
                  </p>
                </div>
              )}

              {/* Preview Caption */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Vista previa del caption:</label>
                <div className="bg-slate-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-sm text-slate-300 whitespace-pre-line">
                    {selectedPub.copy_feed}
                  </p>
                  <p className="text-sm text-blue-400 mt-2">
                    {selectedPub.hashtags_feed?.join(" ")} {getCompanyHashtags()}
                  </p>
                </div>
              </div>

              {/* Publish Results */}
              {publishResults && (
                <div className="space-y-2">
                  <label className="text-sm text-slate-400 mb-2 block">Resultados:</label>
                  {publishResults.map((result, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg flex items-center gap-3 ${
                        result.success
                          ? "bg-green-500/10 border border-green-500/30"
                          : "bg-red-500/10 border border-red-500/30"
                      }`}
                    >
                      {result.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <div>
                        <p className={`font-medium capitalize ${result.success ? "text-green-400" : "text-red-400"}`}>
                          {result.platform}
                        </p>
                        <p className="text-sm text-slate-400">
                          {result.success ? result.message : result.error}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-slate-700 flex gap-3">
              <button
                onClick={() => setShowSocialModal(false)}
                className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={publishToSocial}
                disabled={publishingId === selectedPub.id || selectedPlatforms.length === 0}
                data-testid="confirm-publish-social"
                className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 font-medium transition-all ${
                  publishingId === selectedPub.id || selectedPlatforms.length === 0
                    ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white"
                }`}
              >
                {publishingId === selectedPub.id ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Publicando...
                  </>
                ) : (
                  <>
                    <Share2 className="w-5 h-5" />
                    Publicar Ahora
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reel Upload Modal */}
      {showReelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Film className="w-5 h-5 text-purple-500" />
                Crear Publicación de Reel
              </h3>
              <button
                onClick={() => setShowReelModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-5">
              {/* Step 1: Upload Video */}
              <div className="space-y-3">
                <label className="text-sm text-slate-400 flex items-center gap-2">
                  <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                  Subir Video / Reel
                </label>
                
                {!videoFile ? (
                  <div 
                    onClick={() => videoInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
                  >
                    <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400">Haz clic para seleccionar un video</p>
                    <p className="text-slate-500 text-sm mt-1">MP4, MOV, AVI, WebM (máx. 100MB)</p>
                  </div>
                ) : (
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="relative w-32 h-20 bg-black rounded overflow-hidden">
                        {videoPreviewUrl && (
                          <video src={videoPreviewUrl} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Play className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium truncate">{videoFile.name}</p>
                        <p className="text-slate-400 text-sm">
                          {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        {uploadedVideoId && (
                          <p className="text-green-400 text-sm flex items-center gap-1 mt-1">
                            <CheckCircle2 className="w-4 h-4" /> Video subido
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setVideoFile(null);
                          setVideoPreviewUrl(null);
                          setUploadedVideoId(null);
                        }}
                        className="p-2 text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {!uploadedVideoId && (
                      <div className="mt-3 space-y-2">
                        {/* Progress Bar */}
                        {uploadingVideo && (
                          <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-300 ease-out"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        )}
                        
                        <button
                          onClick={uploadVideo}
                          disabled={uploadingVideo}
                          className="w-full py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                        >
                          {uploadingVideo ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Subiendo... {uploadProgress}%
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5" />
                              Subir Video
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                  onChange={handleVideoSelect}
                  className="hidden"
                />
              </div>

              {/* Step 2: Generate Copy */}
              {uploadedVideoId && (
                <div className="space-y-3">
                  <label className="text-sm text-slate-400 flex items-center gap-2">
                    <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                    Generar Copy con IA
                  </label>
                  
                  <button
                    onClick={generateReelCopy}
                    disabled={generatingCopy}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-slate-600 disabled:to-slate-600 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                  >
                    {generatingCopy ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analizando video y generando copy...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generar Copy
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Step 3: Edit Content */}
              {reelData.copy && (
                <div className="space-y-4">
                  <label className="text-sm text-slate-400 flex items-center gap-2">
                    <span className="bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                    Editar Contenido
                  </label>
                  
                  {/* Name */}
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Nombre de la publicación</label>
                    <input
                      type="text"
                      value={reelData.name}
                      onChange={(e) => setReelData({...reelData, name: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="Ej: Retroexcavadora 420F en acción"
                    />
                  </div>
                  
                  {/* Copy */}
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Copy para Feed</label>
                    <textarea
                      value={reelData.copy}
                      onChange={(e) => setReelData({...reelData, copy: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                      placeholder="Copy para la publicación..."
                    />
                  </div>
                  
                  {/* Hashtags */}
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Hashtags</label>
                    <input
                      type="text"
                      value={reelData.hashtags.join(" ")}
                      onChange={(e) => setReelData({...reelData, hashtags: e.target.value.split(" ").filter(h => h)})}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-blue-400 focus:outline-none focus:border-purple-500"
                      placeholder="#Maquinaria #Construcción..."
                    />
                  </div>
                  
                  {/* Fixed Comment */}
                  <div>
                    <label className="text-sm text-slate-500 mb-1 block">Comentario Fijo</label>
                    <textarea
                      value={reelData.fixedComment}
                      onChange={(e) => setReelData({...reelData, fixedComment: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-slate-700 flex gap-3">
              <button
                onClick={() => setShowReelModal(false)}
                className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveReelPublication}
                disabled={!uploadedVideoId || !reelData.name || !reelData.copy || savingReel}
                className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 font-medium transition-all ${
                  !uploadedVideoId || !reelData.name || !reelData.copy || savingReel
                    ? "bg-slate-600 text-slate-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                }`}
              >
                {savingReel ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Guardar Publicación
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Motion Modal */}
      {showMotionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-cyan-500" />
                Crear Motion - Generar Prompts
              </h3>
              <button
                onClick={() => setShowMotionModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-5">
              {/* Step 1: Select Image from Gallery */}
              <div className="space-y-3">
                <label className="text-sm text-slate-400 flex items-center gap-2">
                  <span className="bg-cyan-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                  Seleccionar imagen
                </label>
                
                {loadingGallery ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
                  </div>
                ) : storiesGallery.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay imágenes disponibles</p>
                    <p className="text-sm">Genera publicaciones primero para tener imágenes</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-5 gap-3 max-h-72 overflow-y-auto p-2 bg-slate-800/50 rounded-lg">
                    {storiesGallery.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedMotionImage(img)}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-[9/16] ${
                          selectedMotionImage?.image_url === img.image_url
                            ? "border-cyan-500 ring-2 ring-cyan-500/30"
                            : "border-slate-700 hover:border-slate-500"
                        }`}
                      >
                        {img.image_url ? (
                          <img
                            src={img.image_url}
                            alt={img.vehicle_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                            <Image className="w-6 h-6 text-slate-500" />
                          </div>
                        )}
                        {selectedMotionImage?.image_url === img.image_url && (
                          <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-cyan-400" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-1">
                          <p className="text-[10px] text-white truncate">{img.vehicle_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 2: Generate Prompts */}
              {selectedMotionImage && (
                <div className="space-y-3">
                  <label className="text-sm text-slate-400 flex items-center gap-2">
                    <span className="bg-cyan-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                    Generar Prompts con IA
                  </label>
                  
                  <div className="flex gap-4">
                    <div className="w-32 aspect-[9/16] rounded-lg overflow-hidden border border-slate-700">
                      <img
                        src={selectedMotionImage.image_url}
                        alt="Selected"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{selectedMotionImage.vehicle_name}</p>
                      <p className="text-slate-400 text-sm">{selectedMotionImage.image_name}</p>
                      
                      <button
                        onClick={generateMotionPrompts}
                        disabled={generatingMotion}
                        className="mt-4 w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-slate-600 disabled:to-slate-600 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                      >
                        {generatingMotion ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Analizando imagen y generando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            Generar Prompts
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Results */}
              {motionData.animationPrompt && (
                <div className="space-y-4">
                  <label className="text-sm text-slate-400 flex items-center gap-2">
                    <span className="bg-cyan-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                    Prompts Generados
                  </label>

                  {/* Animation Prompt */}
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-cyan-400 font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Prompt de Animación (Video)
                      </h4>
                      <button
                        onClick={() => copyMotionText(motionData.animationPrompt, 'animation')}
                        className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        {copiedItem === 'motion-animation' ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap font-mono bg-slate-900 p-3 rounded max-h-40 overflow-y-auto">
                      {motionData.animationPrompt}
                    </p>
                  </div>

                  {/* Voiceover Text */}
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-orange-400 font-medium flex items-center gap-2">
                        <Mic className="w-4 h-4" />
                        Voz en Off
                      </h4>
                      <button
                        onClick={() => copyMotionText(motionData.voiceoverText, 'voiceover')}
                        className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        {copiedItem === 'motion-voiceover' ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-900 p-3 rounded">
                      {motionData.voiceoverText}
                    </p>
                  </div>

                  {/* Suno Prompt */}
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-purple-400 font-medium flex items-center gap-2">
                        <Music className="w-4 h-4" />
                        Prompt para Suno (Música)
                      </h4>
                      <button
                        onClick={() => copyMotionText(motionData.sunoPrompt, 'suno')}
                        className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        {copiedItem === 'motion-suno' ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap font-mono bg-slate-900 p-3 rounded">
                      {motionData.sunoPrompt}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => setShowMotionModal(false)}
                className="px-6 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicationsTab;
